"use client"

import { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import { CourseCard } from "@/components/course-card";
import { CourseCardSkeleton } from "@/components/course-card-skeleton";
import { Home } from "lucide-react";
import Link from "next/link";

// Lazy load Breadcrumb component
const Breadcrumb = dynamic(
  () => import("@/components/ui/breadcrumb").then((mod) => mod.Breadcrumb),
  {
    loading: () => <div className="h-8 animate-pulse bg-muted rounded" />,
    ssr: true,
  }
);
const BreadcrumbItem = dynamic(
  () => import("@/components/ui/breadcrumb").then((mod) => mod.BreadcrumbItem),
  { ssr: true }
);
const BreadcrumbLink = dynamic(
  () => import("@/components/ui/breadcrumb").then((mod) => mod.BreadcrumbLink),
  { ssr: true }
);
const BreadcrumbList = dynamic(
  () => import("@/components/ui/breadcrumb").then((mod) => mod.BreadcrumbList),
  { ssr: true }
);
const BreadcrumbPage = dynamic(
  () => import("@/components/ui/breadcrumb").then((mod) => mod.BreadcrumbPage),
  { ssr: true }
);
const BreadcrumbSeparator = dynamic(
  () => import("@/components/ui/breadcrumb").then((mod) => mod.BreadcrumbSeparator),
  { ssr: true }
);

interface Course {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  price: string;
  duration: string;
  classSize: string;
  startDate: string;
  image: string;
  slug: string;
  signupForm: string;
  highlighted: boolean;
}

export default function CoursesPage() {
  const [coursesData, setCoursesData] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/courses');
        if (!response.ok) {
          throw new Error('Failed to fetch courses');
        }
        const courses = await response.json();
        setCoursesData(courses);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  // Sort courses to show highlighted courses first
  const sortedCourses = coursesData.length > 0
    ? [...coursesData].sort((a: Course, b: Course) => {
        if (a.highlighted && !b.highlighted) return -1;
        if (!a.highlighted && b.highlighted) return 1;
        return 0;
      })
    : [];

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-card to-muted flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Дахин оролдох
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Breadcrumb */}
      {/* <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/" className="flex items-center gap-1">
                    <Home className="w-4 h-4" />
                    Нүүр
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Сургалтууд</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </section> */}

      {/* Header Section */}
      <section className="pb-10 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl   mb-6">
            Сургалтууд
          </h1>
          <p className="text-lg max-w-3xl mx-auto leading-relaxed text-muted-foreground">
            Таны хэрэгцээ, цаг хугацаанд тохирсон хятад хэлний сургалтын хөтөлбөрийг сонгоорой
          </p>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              // Show skeleton loaders while loading
              Array.from({ length: 4 }).map((_, index) => (
                <CourseCardSkeleton key={`skeleton-${index}`} />
              ))
            ) : sortedCourses.length > 0 ? (
              // Render lazy-loaded course cards
              sortedCourses.map((course: Course, index: number) => (
                <CourseCard
                  key={course.id || index}
                  course={course}
                  index={index}
                />
              ))
            ) : (
              // No courses found
              <div className="col-span-full text-center py-12">
                <p className="text-muted-foreground">
                  Сургалт олдсонгүй
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}